/**
 * Jovii — WHAT GOES ON A SHELF, decided once a day rather than once a render.
 *
 * Home, Shows and Movies are the same architecture: a banner, then rows. The rows
 * on Home are not a recommendation engine — there isn't one yet and pretending
 * otherwise would be a lie told in the interface. What there is instead:
 *
 *  1. TASTE, read off this device. The genres of the things you kept and the
 *     things you opened, saved ones counted double. Two rails at most, and each
 *     one says out loud which title it came from.
 *  2. A SEEDED SET of genre rails, so the page is varied without being random.
 *
 * THE SEED IS THE DAY, NOT THE RENDER. A shuffle evaluated per render gives a
 * different page on every reload and on every back-navigation, which makes the
 * screen feel broken — you cannot go back to the row you were just looking at.
 * `daySeed()` hashes the UTC date, so the shelf is fixed from midnight to
 * midnight, identical on every device in the house (worth something when somebody
 * says "look at the horror row"), and different tomorrow.
 *
 * Everything here is a pure function of its arguments. No fetching, no runes, no
 * DOM — which is what makes `shelf.test.ts` able to assert on the actual page
 * rather than on a description of it.
 */

import type { CatalogItem, MediaKind } from '$types';
import { genresFor, type CatalogId } from './cinemeta';

/* ────────────────────────────────────────────────────────────────────────────
 * A seed, and a shuffle that follows from it
 * ──────────────────────────────────────────────────────────────────────────── */

import { hash } from './hash';
export { hash } from './hash';

/** mulberry32: 32 bits of state, uniform enough for choosing six genres. */
export function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The current day as a seed. UTC on purpose: two people in one house on two
 * devices should be looking at the same shelf, and a local-midnight boundary
 * would put a phone and a laptop on different pages for a few hours.
 */
export function daySeed(now: number | Date = Date.now()): number {
  const d = new Date(now);
  return hash(`jove-shelf:${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`);
}

/** Fisher–Yates, driven by a supplied generator so the result is reproducible. */
export function shuffled<T>(list: readonly T[], rand: () => number): T[] {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Taste, as far as a browser can honestly know it
 * ──────────────────────────────────────────────────────────────────────────── */

export interface Taste {
  genre: string;
  kind: MediaKind;
  /** The title this came from, so a rail can say why it is there. */
  because: string;
  /** How many times this genre/kind pair was counted. Ordering only. */
  weight: number;
}

/**
 * What this device has seen you do, as genres.
 *
 * KEEPING SOMETHING IS A STRONGER STATEMENT THAN OPENING IT, so a saved title is
 * counted twice and an opened one once. Ties break towards the more recent list
 * position, because both arrays are newest-first.
 *
 * Progress entries are deliberately NOT a source: `Progress` carries a title and
 * a position but no genres, and inventing them would mean 60 catalogue lookups
 * to draw a home page.
 */
export function taste(saved: readonly CatalogItem[], opened: readonly CatalogItem[], limit = 3): Taste[] {
  const score = new Map<string, Taste>();

  const count = (item: CatalogItem, times: number): void => {
    for (const genre of item.genres ?? []) {
      const key = `${item.kind}:${genre}`;
      const seen = score.get(key);
      if (seen) seen.weight += times;
      else score.set(key, { genre, kind: item.kind, because: item.name, weight: times });
    }
  };

  for (const item of saved) count(item, 2);
  for (const item of opened) count(item, 1);

  return [...score.values()].sort((a, b) => b.weight - a.weight).slice(0, limit);
}

/* ────────────────────────────────────────────────────────────────────────────
 * The rails
 * ──────────────────────────────────────────────────────────────────────────── */

export interface RailSpec {
  /** Stable across renders for the same day, so `{#each}` keys do not churn. */
  key: string;
  title: string;
  /** A quiet second line on the heading — "because you saved Dune". */
  note?: string;
  kind: MediaKind;
  catalog: CatalogId;
  genre?: string;
}

/**
 * The genres a household evening is actually chosen from.
 *
 * All 22 of Cinemeta's genres are one tap away on `/categories` and every one of
 * them has its own rail on `/movies` and `/shows`. This shorter list is only the
 * pool Home draws its surprise rows from: a shuffle over the full vocabulary
 * spends a third of the page on Sport, Biography, History and War, which is a
 * worse home page than the same six slots spent on Comedy and Horror. The
 * excluded genres are not hidden, they are just not the lottery.
 */
const EVENING = [
  'Action',
  'Adventure',
  'Animation',
  'Comedy',
  'Crime',
  'Documentary',
  'Drama',
  'Family',
  'Fantasy',
  'Horror',
  'Mystery',
  'Romance',
  'Sci-Fi',
  'Thriller'
] as const;

/**
 * Home's rows, in order: at most two taste rails, a fixed spine of six
 * (popular · new · highest rated, both kinds) and SIX seeded genre rows folded
 * in between them.
 *
 * The spine never moves. It is the answer to "what is everyone watching", and a
 * page whose skeleton changes daily is a page nobody ever learns to use. Only
 * the genre rows rotate, and only once a day.
 */
export function homeRails(seed: number, likes: readonly Taste[] = []): RailSpec[] {
  const rand = prng(seed);
  const spoken = new Set(likes.map((t) => t.genre));
  const pool = shuffled(EVENING, rand).filter((g) => !spoken.has(g));

  const first = likes[0];
  const second = likes[1];
  let picked = 0;
  const surprise = (kind: MediaKind): RailSpec | null => {
    const genre = pool[picked++];
    if (!genre) return null;
    return {
      key: `genre-${kind}-${genre}`,
      title: kind === 'series' ? `${genre} series` : `${genre} films`,
      kind,
      catalog: 'top',
      genre
    };
  };

  const rails: (RailSpec | null)[] = [
    first
      ? {
          key: `taste-${first.kind}-${first.genre}`,
          title: 'Your next watch',
          note: `more ${first.genre}, because you kept ${first.because}`,
          kind: first.kind,
          catalog: 'top',
          genre: first.genre
        }
      : null,
    { key: 'top-movie', title: 'Popular films', kind: 'movie', catalog: 'top' },
    { key: 'top-series', title: 'Popular series', kind: 'series', catalog: 'top' },
    surprise('movie'),
    { key: 'year-movie', title: 'New films this year', kind: 'movie', catalog: 'year' },
    surprise('series'),
    second
      ? {
          key: `taste-${second.kind}-${second.genre}`,
          title: `More ${second.genre}`,
          note: `because you kept ${second.because}`,
          kind: second.kind,
          catalog: 'top',
          genre: second.genre
        }
      : null,
    { key: 'rating-series', title: 'Highest rated series', kind: 'series', catalog: 'imdbRating' },
    surprise('movie'),
    { key: 'year-series', title: 'New series this year', kind: 'series', catalog: 'year' },
    surprise('series'),
    { key: 'rating-movie', title: 'Highest rated films', kind: 'movie', catalog: 'imdbRating' },
    surprise('movie'),
    surprise('series')
  ];

  // `surprise()` answers null once the pool is spent, which is how a taste that
  // covers half of EVENING still produces a coherent page instead of a hole.
  return rails.filter((r): r is RailSpec => r !== null);
}

/**
 * One kind's shelf — `/movies` and `/shows`. POPULAR, then NEW, then every
 * genre the catalogue has for this kind, in the catalogue's own order.
 *
 * Deliberately NOT seeded. Home is the room you wander into; this is the shelf
 * you go to when you are looking for something, and a shelf whose order moves
 * overnight is a shelf you cannot learn.
 */
export function browseRails(kind: MediaKind): RailSpec[] {
  const label = kind === 'series' ? 'series' : 'films';
  return [
    { key: 'top', title: `Popular ${label}`, kind, catalog: 'top' },
    { key: 'year', title: 'New this year', kind, catalog: 'year' },
    { key: 'rating', title: 'Highest rated', kind, catalog: 'imdbRating' },
    ...genresFor(kind).map(
      (genre): RailSpec => ({ key: `genre-${genre}`, title: genre, kind, catalog: 'top', genre })
    )
  ];
}

/* ────────────────────────────────────────────────────────────────────────────
 * The banner
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Which titles get to be a slide.
 *
 * Pools are consumed round-robin, so a Home banner built from `[films, series]`
 * alternates instead of showing six films. Two passes: everything with a
 * wordmark AND a sentence first, then whatever is left to fill the count. A
 * title with no logo is still a legal slide — the banner sets its name in
 * display type instead — it is just not the one you lead with.
 */
export function heroPicks(
  pools: readonly (readonly CatalogItem[])[],
  limit = 6,
  seed?: number
): CatalogItem[] {
  const seen = new Set<string>();
  const out: CatalogItem[] = [];
  const depth = Math.max(0, ...pools.map((p) => p.length));

  for (const wanted of [(x: CatalogItem) => !!(x.logo && x.description), () => true]) {
    for (let i = 0; i < depth && out.length < limit; i++) {
      for (const pool of pools) {
        if (out.length >= limit) break;
        const item = pool[i];
        if (!item || seen.has(item.id) || !wanted(item)) continue;
        seen.add(item.id);
        out.push(item);
      }
    }
    if (out.length >= limit) break;
  }

  /*
   * SHUFFLED, ONCE A SEED IS GIVEN.
   *
   * Without one this is the old behaviour exactly — the first N that qualify,
   * which meant the banner opened on the same handful of titles every time and
   * the catalogue looked smaller than it is. With a seed it gathers a WIDER
   * candidate pool (see the caller) and deals from it, so the six change while
   * staying fixed for the day like every other shelf on the page. Same
   * `mulberry32` as the rails, so a day's banner is reproducible from its date.
   */
  if (seed === undefined) return out.slice(0, limit);
  const rng = prng(seed);
  const deck = out.slice();
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [deck[i], deck[j]] = [deck[j]!, deck[i]!];
  }
  return deck.slice(0, limit);
}

/*
 * Collections used to live here as genre queries wearing curated names — a tile
 * called "Detectives" that opened the Crime shelf. They are real now: membership
 * resolved from Wikidata and verified against the catalogue at build time. See
 * `collections.ts` and `scripts/fetch-collections.mjs`.
 */
